import {Component, OnInit, ViewChild} from '@angular/core';
import {UserPrivilegeService} from '../../services/common/user-privilege/user-privilege.service';
import {ServersApiService} from '../../services/api/users/servers/servers-api.service';
import {DisplayedColumnInf} from '../../interfaces/common/table/displayed-column-inf';
import {TableColumnsConfigurationDialogComponent} from '../../dialogs/table-columns-configuration-dialog/table-columns-configuration-dialog.component';
import {MatDialog} from '@angular/material/dialog';
import {AttributeManipulationGroupsApiService} from '../../services/api/settings/call-flow-configurations/attribute-manipulation-groups/attribute-manipulation-groups-api.service';
import {ServerOperativeStatePipe} from './server-operative-state.pipe';
import {DateTimePipe} from '../../pipes/date-time/date-time.pipe';
import {ConfirmationDialogComponent} from '../../dialogs/confirmation-dialog/confirmation-dialog.component';
import {SnackBarService} from '../../services/common/snack-bar/snack-bar.service';
import {LogicalConjunctionService} from '../../services/common/logical-conjunction/logical-conjunction.service';
import * as _ from 'lodash';
import {ServersDialogComponent} from './servers-dialog/servers-dialog.component';
import {ServerTypePipe} from './server-type.pipe';
import {ArmTableComponent} from '../../components/arm-table/arm-table.component';
import {LoaderService} from '../../services/common/loader-service/loader.service';
import {RoleTypeEnum} from '../../services/enums/role-type.enum';

@Component({
  selector: 'servers',
  templateUrl: './servers.component.html',
  styleUrls: ['./servers.component.scss']
})
export class ServersComponent implements OnInit {

  constructor(private userPrivilege: UserPrivilegeService,
              private serversApiService: ServersApiService,
              public dialog: MatDialog,
              private attributeManipulationGroupsApiService: AttributeManipulationGroupsApiService,
              private snackBarService: SnackBarService,
              private logicalConjunctionService: LogicalConjunctionService,
              private loaderService: LoaderService) { }

  isUnauthorized;
  data;
  selection: any;
  filter;

  @ViewChild(ArmTableComponent) armTable;

  displayedColumns: Array<DisplayedColumnInf> = [
    {name: 'type', display: 'Type', selected: true, transformer: ServerTypePipe, width: 100},
    {name: 'status', display: 'Status', selected: true, transformer: ServerOperativeStatePipe, width: 100},
    {name: 'name', display: 'Name', selected: true, width: 200},
    {name: 'numOfUsers', display: 'Number of users', selected: true, width: 200},
    {name: 'lastSyncTimestamp', display: 'Last successful update', selected: true, transformer: DateTimePipe, width: 200},
    {name: 'lastFullSyncTimestamp', display: 'Last successful full update', selected: true, transformer: DateTimePipe, width: 300},
  ];
  normalizationGroupsOptions: any;

  ngOnInit(): void {
    this.isUnauthorized = !this.userPrivilege.isAuthorized(RoleTypeEnum.USER_MANAGEMENT);
    this.refresh();
  }

  refresh(): void {
    this.loaderService.showMainContainer();
    Promise.all([this.serversApiService.getServers(),
      this.attributeManipulationGroupsApiService.getAttributeManipulationGroups().toPromise()])
      .then((data) => {
        this.data = data[0];
        this.normalizationGroupsOptions = data[1];
        this.loaderService.hideMainContainer();
      })
      .catch(() => {
        this.loaderService.hideMainContainer();
      });
  }

  addItem(ldapType: string): void {
    this.serversApiService.prepareDataForServerAdd(ldapType).then(data => {
      const dialogRef = this.dialog.open(ServersDialogComponent, {
        width: '65%',
        height: '760px',
        data: {
          isEditMode: false,
          selectedItem: data[0],
          attributes: data[1],
          normalizationGroupsOptions: data[2],
          type: ldapType
        }
      });

      dialogRef.componentInstance.onApply.subscribe(result => {
        const serverWithoutEmptyMappings = this.removeEmptyMappings(result);
        this.serversApiService.addServer(serverWithoutEmptyMappings).then(() => {
          dialogRef.close();
          this.snackBarService.display(`"${result.name}" successfully added`);
          this.refresh();
        });
      });
    });
  }

  editItem(): void {

    this.serversApiService.prepareDataForServerEdit(this.selection.id).then(data => {
      const dialogRef = this.dialog.open(ServersDialogComponent, {
        width: '65%',
        height: '760px',
        data: {
          isEditMode: true,
          selectedItem: data[0],
          attributes: data[1],
          normalizationGroupsOptions: data[2],
        }
      });

      dialogRef.componentInstance.onApply.subscribe(result => {
        const serverWithoutEmptyMappings = this.removeEmptyMappings(result);
        this.serversApiService.editServer(serverWithoutEmptyMappings).then(() => {
          dialogRef.close();
          this.snackBarService.display(`"${result.name}" successfully edited`);
          this.refresh();
        });
      });
    });
  }

  removeItem(): void {
    const confirmationMessage = `Are you sure you want to delete the server: ${this.selection.name} ?`;

    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      data: {
        message: confirmationMessage
      }
    });

    dialogRef.componentInstance.onApply.subscribe(() => {
      this.serversApiService.deleteServer(this.selection.id).then(() => {
        dialogRef.close();
        this.refresh();
        this.snackBarService.display(`${this.logicalConjunctionService
          .generateAndDescription(this.selection, 'name')} successfully deleted`);
      }).catch((error) => {
        console.log(`Exception was thrown in remove server ${this.selection.name}, reason: ${error}`);
      });
    });
  }

  select(item: any): void {
    this.selection = item;
  }

  openColumnsConfiguration(): void {
    const dialogRef = this.dialog.open(TableColumnsConfigurationDialogComponent, {
      width: '350px',
      data: {
        columns: this.displayedColumns
      }
    });

    dialogRef.componentInstance.onApply.subscribe(() => {
      dialogRef.close();
    });
  }

  onFilter(filter: any): any {
    this.armTable.firstPage();
    this.filter = filter;
  }

  removeEmptyMappings(server): any {
    const serverWithoutEmptyMappings = _.cloneDeep(server);
    serverWithoutEmptyMappings.dicAttrMapping = serverWithoutEmptyMappings.dicAttrMapping.filter((mapping) => {
      return mapping.aDAttrName;
    });

    return serverWithoutEmptyMappings;
  }
}
